import type {
	ColorRule,
	ColumnConfiguration,
	FieldConfiguration,
} from '../types';

/*
 * PD-17191 — the person columns shared by the "Ecommerce Users" tabs of
 * Settings > Login History and Settings > Activity Logs: the date with the
 * user's name under it, and a Contact column with the email over the phone.
 *
 * Both lists read flat `userEmail` / `userPhone` fields that the API fills in
 * ready to display (the phone already formatted), so the two tabs show a
 * person the same way. The CRM Users tabs keep their own columns.
 *
 * The headers are written as one entry per stacked value (Date / User,
 * Email / Phone) because the CSV export pairs each stacked field with the
 * non-line-break header in the same position, so the export reads
 * "Date, User, Email, Phone" rather than repeating a column name.
 */

/** Separates the stacked values of one cell onto their own lines. */
const lineBreakField = (id: string): FieldConfiguration => ({
	id,
	dataConfig: { type: 'custom_text', customText: '' },
	styleConfig: { size: 'md', colorRule: { type: 'default' } },
	displayStyle: 'line_break',
});

/**
 * A value in its usual color when present, and the muted color when its
 * placeholder ("No email", "No phone") shows instead. The API sends a missing
 * value as null, so "not empty" is the presence test; the conditional rule
 * reads the row's own flat field, before the placeholder is applied.
 */
const presentOrMutedColor = (
	fieldName: string,
	presentThemeColor: 'textPrimary' | 'textSecondary',
): ColorRule => ({
	type: 'conditional',
	rules: [
		{
			field: { fieldName },
			operator: 'not_equals',
			value: '',
			color: { themeColor: presentThemeColor },
		},
	],
	defaultColor: { themeColor: 'textMuted' },
});

/**
 * The date of the login or activity, with the user's name on a second,
 * secondary line.
 */
export const buildEcommerceDateAndUserColumn = (config: {
	objectTable: string;
	nameTemplate: string;
}): ColumnConfiguration => ({
	id: 'date',
	headers: [
		{ id: 'date-header', text: 'Date' },
		{ id: 'date-break', text: '', displayStyle: 'line_break' },
		{ id: 'date-user-header', text: 'User' },
	],
	fields: [
		{
			id: 'date-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'date', objectTable: config.objectTable },
			},
			styleConfig: {
				size: 'md',
				colorRule: { type: 'default' },
				formatOptions: { dateFormat: 'datetime-short' },
				allowTextWrap: false,
			},
			displayStyle: 'date',
		},
		lineBreakField('line-break-date'),
		{
			id: 'user-name-field',
			dataConfig: {
				type: 'template',
				template: config.nameTemplate,
				templateFields: [],
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
				// A long name is cut off with an ellipsis and shown in full in
				// a tooltip, rather than widening the fixed date column.
				textExpansion: { enabled: true, maxLines: 1 },
			},
			displayStyle: 'text',
		},
	],
	sortable: true,
	sortFields: [
		{
			objectTable: config.objectTable,
			fieldName: 'date',
			direction: 'DESC',
		},
	],
	width: 180,
	minWidth: 180,
	maxWidth: 180,
	verticalAlignment: 'top',
});

/**
 * The person's email with their phone under it. A missing value shows a
 * muted "No email" / "No phone" rather than an empty line.
 */
export const buildEcommerceContactColumn = (config: {
	objectTable: string;
}): ColumnConfiguration => ({
	id: 'contact',
	label: 'Contact',
	headers: [
		{ id: 'contact-email-header', text: 'Email' },
		{ id: 'contact-break', text: '', displayStyle: 'line_break' },
		{ id: 'contact-phone-header', text: 'Phone' },
	],
	fields: [
		{
			id: 'contact-email-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'userEmail', objectTable: config.objectTable },
				defaultValue: 'No email',
			},
			styleConfig: {
				size: 'md',
				colorRule: presentOrMutedColor('userEmail', 'textPrimary'),
			},
			displayStyle: 'text',
		},
		lineBreakField('line-break-contact'),
		{
			id: 'contact-phone-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'userPhone', objectTable: config.objectTable },
				defaultValue: 'No phone',
			},
			styleConfig: {
				size: 'sm',
				colorRule: presentOrMutedColor('userPhone', 'textSecondary'),
			},
			displayStyle: 'text',
		},
	],
	sortable: false,
	widthConfig: {
		mode: 'flexible',
		preset: 'large',
	},
	verticalAlignment: 'top',
});
